import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle } from "react-feather";

import UserPolicyModal from "../UserPolicyModal/UserPolicyModal";

const JOB_STATUS_OPTIONS = [
  { value: "", label: "Select job status" },
  { value: "part-time", label: "Part-time" },
  { value: "full-time", label: "Full-time" },
  { value: "self-employed", label: "Self-employed" },
  { value: "unemployed", label: "Unemployed" },
  { value: "employed", label: "Employed" },
];

const JobStatus = ({ show, onClose, onConfirm, selectedJobStatus = "", userData }) => {
  const navigate = useNavigate();
  const [jobStatus, setJobStatus] = useState(selectedJobStatus || "");
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const validateForm = () => {
    if (!jobStatus) {
      setError("Please select a job status");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (onConfirm) {
        await onConfirm({
          jobStatus,
          policyId: selectedPolicyId || undefined,
        });
        setSuccess("Job status updated successfully!");

        setTimeout(() => {
          onClose();
          navigate("/user-management", { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err.message || "Failed to update job status. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title d-flex align-items-center">
              <i className="mdi mdi-briefcase-account me-2"></i>
              Update Employment Details
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {error && (
              <div
                className="alert alert-danger d-flex align-items-center"
                role="alert"
              >
                <AlertCircle className="flex-shrink-0 me-2" size={18} />
                <div>{error}</div>
              </div>
            )}

            {success && (
              <div
                className="alert alert-success d-flex align-items-center"
                role="alert"
              >
                <CheckCircle className="flex-shrink-0 me-2" size={18} />
                <div>{success}</div>
              </div>
            )}

            {/* Job Status Section */}
            <div className="mb-4">
              <label className="form-label fw-semibold">
                Current Employment Status <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${!jobStatus && "is-invalid"}`}
                value={jobStatus}
                onChange={(e) => {
                  setJobStatus(e.target.value);
                  setError("");
                }}
                disabled={isSubmitting}
              >
                <option value="">-- Select your employment status --</option>
                {JOB_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {!jobStatus && (
                <div className="invalid-feedback">
                  Please select your employment status
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold d-flex justify-content-between align-items-center">
                <span>Linked Policy (optional)</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowPolicyModal(true)}
                  disabled={isSubmitting || !userData?._id}
                >
                  <i className="mdi mdi-file-document-edit-outline me-1"></i>
                  Select Policy
                </button>
              </label>
              <div className="border rounded p-2 bg-light">
                {selectedPolicyId ? (
                  <span className="small">Selected Policy ID: {selectedPolicyId}</span>
                ) : (
                  <span className="text-muted small">No policy selected</span>
                )}
              </div>
            </div>

            <UserPolicyModal
              show={showPolicyModal}
              onClose={() => setShowPolicyModal(false)}
              userId={userData?._id}
              selectedPolicyId={selectedPolicyId}
              onSelect={(policyId) => {
                setSelectedPolicyId(policyId);
                setShowPolicyModal(false);
              }}
            />
          </div>
          <div className="modal-footer bg-light">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              <i className="mdi mdi-close-circle-outline me-1"></i>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !jobStatus}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="mdi mdi-content-save-check me-1"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobStatus;